/**
 * Idea: Make headers fixed with top: 0, but translate them up when scrolling down
 *
 * Strategy:
 * - Detect change in scroll direction
 * - On scroll, calculate position offset since last direction change,
 *   write that to global --scroll-offset
 * - For each element with [data-scroll-offset]:
 *   - Calculate their current translation when direction changes
 *     and write that to their local --current-offset
 *   - On scroll, calculate their new translation based on --scroll-offset and --current-offset
 */

import { select } from '@js/util/select.js'

let previousValue = window.scrollY
let previousDirection = 1
let previousDirectionChangeValue = window.scrollY

function scrollHandler() {
  const value = Math.max(window.scrollY, 0)
  const diff = value - previousValue
  const direction = value === previousValue ? previousDirection : value > previousValue ? 1 : -1
  const directionChanged = direction !== previousDirection

  const offsetToLatestDirectionChange = value - previousDirectionChangeValue

  if (directionChanged) {
    previousDirectionChangeValue = value
    previousDirection = direction
  }

  previousValue = value

  requestAnimationFrame(() => {
    const root = window.document.documentElement

    if (directionChanged) {
      const directionString = direction === 1 ? 'down' : 'up'

      window.dispatchEvent(
        new CustomEvent('scroll-direction-change', {
          detail: {
            direction: directionString,
            offsetToLatestDirectionChange,
          },
        }),
      )
    }

    if (value === 0 && diff < 0) {
      window.dispatchEvent(new CustomEvent('scroll-reach-top'))
    }

    root.style.setProperty('--scroll-offset', `${offsetToLatestDirectionChange}px`)
  })
}

let counter = 0
select('[data-scroll-offset]', element => {
  if (counter === 0) {
    window.addEventListener('scroll', scrollHandler, { passive: true })
  }
  counter++

  const getOffset = () =>
    getComputedStyle(element)
      .transform.match(/,([^,]*)\)/)?.[1]
      .trim() ?? '0'

  let wasZeroOffset = getOffset() === '0'
  element.classList.toggle('no-scroll-offset', wasZeroOffset)

  // This is a safety fallback needed for the case when the user scrolls to the top very fast
  // on mobile devices where the 'scroll' event is throttled, and the 'scroll-direction-change'
  // event thus yields misleading data
  window.addEventListener('scroll-reach-top', function reachTopHandler() {
    element.style.setProperty('--current-offset', '0px')
  })

  window.addEventListener('scroll-direction-change', function scrollDirectionChangeHandler() {
    const offset = getOffset()

    element.style.setProperty('--current-offset', `${offset}px`)

    const isZeroOffset = offset === '0'
    if (isZeroOffset !== wasZeroOffset) {
      element.classList.toggle('no-scroll-offset', isZeroOffset)
      wasZeroOffset = isZeroOffset
    }
  })

  return () => {
    counter--

    window.removeEventListener('scroll-reach-top', reachTopHandler)
    window.removeEventListener('scroll-direction-change', scrollDirectionChangeHandler)

    if (counter === 0) {
      window.removeEventListener('scroll', scrollHandler)
    }
  }
})
